/**
 * 예산변경 공통 워크스페이스
 * - register: 작성·저장·승인요청
 * - approve: 요청건 승인/반려 (배정처리 YN에 따라 배정 동시/별도)
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer } from 'styles/common.styles';
import { theme, SEARCH_LABEL_W } from 'styles/theme';
import {
  FormArea,
  CollapsibleSearchPanel,
  ActionButtonGroup,
  getCurrentFiscalYear,
} from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  ActiveGridProvider,
  ActiveGridScope,
  ActiveGridHeader,
  useActiveGridCrudActions,
} from 'contexts/ActiveGridContext';
import { showAlert, showConfirm } from 'utils/toastBridge';
import { listBudgetKindOptions } from 'budget/budgetRoundStore';
import { loadEnvSettings } from 'system/envSettingsStore';
import CodeLookupDialog from 'components/budget/CodeLookupDialog';
import { loadDepts, filterDepts } from 'master/deptStore';
import { loadOrSeedProjects } from 'budget/budgetProjectStore';
import { loadSubjectsByFilter } from 'budget/budgetStructureStore';
import {
  buildSubjectDispCode,
  buildSubjectDispName,
} from 'budget/budgetSubjectHistoryStore';
import { loadFunds } from 'budget/budgetFundStore';
import { loadBalances, filterBalances } from 'budget/budgetBalanceStore';
import {
  CHANGE_MATCH_MODE,
  CHANGE_TYPE_OPTIONS,
  CHANGE_STATUS,
  CHANGE_STATUS_OPTIONS,
  ASSIGN_PROCESS_OPTIONS,
  changeTypeLabel,
  changeStatusLabel,
  changeMatchModeLabel,
  createChangeUid,
  createChangeDetailUid,
  emptyMasterRow,
  emptyDetailOneToOne,
  emptyDetailNn,
  nextChangeNo,
  loadChangeBundle,
  saveChangeBundle,
  calcMasterChangeAmt,
  validateChangeBundle,
  requestApproval,
  approveChange,
  rejectChange,
  filterMastersForApprove,
  filterMastersForRegister,
  buildAllocLinesFromChange,
  todayYmd,
  normalizeMasterDates,
} from 'budget/budgetChangeStore';
import { appendAllocFromBudgetChange } from 'budget/budgetAllocationStore';

const MASTER_GRID_ID = 'budget-change-master';
const DETAIL_GRID_ID = 'budget-change-detail';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
  height: 100%;
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: ${(p) => p.$flex || 1};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  overflow: hidden;
  background: ${theme.colors.white};
`;

const GridBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const Hint = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  padding: 0 2px;
`;

function emptySearch(partial = {}) {
  return {
    fiscalYear: getCurrentFiscalYear(),
    budgetKindCode: '',
    inoutDiv: '지출',
    status: '',
    changeType: '',
    ...partial,
  };
}

/**
 * @param {{ screenMode?: 'register'|'approve' }} props
 */
function BudgetChangeWorkspaceInner({ screenMode = 'register' }) {
  const isApprove = screenMode === 'approve';
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);

  const matchMode = useMemo(() => {
    const env = loadEnvSettings(tenantCode);
    return env?.budget?.changeMatchMode === CHANGE_MATCH_MODE.N_TO_N
      ? CHANGE_MATCH_MODE.N_TO_N
      : CHANGE_MATCH_MODE.ONE_TO_ONE;
  }, [tenantCode]);

  const isNn = matchMode === CHANGE_MATCH_MODE.N_TO_N;

  const kindOptions = useMemo(() => {
    const opts = listBudgetKindOptions(tenantCode) || [];
    return opts.map((o) => ({
      value: String(o.value ?? o.code ?? ''),
      label: String(o.label ?? o.name ?? o.value ?? ''),
    }));
  }, [tenantCode]);

  const searchDefaults = useMemo(
    () =>
      emptySearch({
        budgetKindCode: kindOptions[0]?.value || '',
        status: isApprove ? CHANGE_STATUS.REQUESTED : '',
      }),
    [kindOptions, isApprove]
  );

  const methods = useForm({ mode: 'onSubmit', defaultValues: searchDefaults });
  const { getValues, reset } = methods;

  const masterRef = useRef(null);
  const detailRef = useRef(null);
  const [selectedUid, setSelectedUid] = useState('');
  const selectedUidRef = useRef('');
  const [bundleDetails, setBundleDetails] = useState([]);
  /** 디테일 코드·현액 조회 팝업 */
  const [lookup, setLookup] = useState({
    open: false,
    type: null,
    side: '',
    detailUid: '',
  });

  useEffect(() => {
    selectedUidRef.current = selectedUid;
  }, [selectedUid]);

  const openDetailLookup = useCallback(
    (type, row, side = '') => {
      if (isApprove) return;
      if (!row?.detailUid) {
        showAlert('디테일 행을 선택한 뒤 조회하세요.', 'warning');
        return;
      }
      setLookup({ open: true, type, side: side || '', detailUid: row.detailUid });
    },
    [isApprove]
  );

  const patchDetailRow = useCallback((detailUid, patch) => {
    if (!detailUid || !patch) return;
    const data = detailRef.current?.getData?.() || [];
    detailRef.current?.setData?.(
      data.map((r) => {
        if (r.detailUid !== detailUid) return r;
        return {
          ...r,
          ...patch,
          mod: r.mod === 'A' ? 'A' : 'U',
        };
      })
    );
  }, []);

  const { handleActiveAdd, handleActiveDelete, activeLabel, activeGridId } = useActiveGridCrudActions({
    guardAdd: (id) => {
      if (isApprove) return '승인 화면에서는 추가할 수 없습니다.';
      if (id === DETAIL_GRID_ID && !selectedUidRef.current) {
        return '마스터를 먼저 선택하세요.';
      }
      return null;
    },
    guardDelete: () => (isApprove ? '승인 화면에서는 삭제할 수 없습니다.' : null),
  });

  const masterColumns = useMemo(() => {
    const typeCol = isApprove
      ? hiCol.roTextColumn({ header: '변경구분', field: 'changeTypeNm', size: 100, align: 'center' })
      : hiCol.edComboColumn({
          header: '변경구분',
          field: 'changeType',
          size: 100,
          options: CHANGE_TYPE_OPTIONS,
        });
    const assignCol = isApprove
      ? hiCol.roTextColumn({ header: '배정처리', field: 'assignProcessYn', size: 80, align: 'center' })
      : hiCol.edComboColumn({
          header: '배정처리',
          field: 'assignProcessYn',
          size: 120,
          options: ASSIGN_PROCESS_OPTIONS,
        });
    return [
      hiCol.roTextColumn({ header: '변경번호', field: 'changeNo', size: 80, align: 'center' }),
      typeCol,
      assignCol,
      hiCol.edNumberColumn({
        header: '변경금액',
        field: 'changeAmt',
        size: 100,
        align: 'right',
        editable: false,
      }),
      hiCol.edNumberColumn({
        header: '승인금액',
        field: 'approveAmt',
        size: 100,
        align: 'right',
        editable: isApprove,
      }),
      hiCol.roTextColumn({ header: '상태', field: 'statusNm', size: 80, align: 'center' }),
      // 변경일자: 작성중·반려만 편집. 오늘 자동채움 없음 · 소급 직접 지정
      hiCol.edDateColumn({
        header: '변경일자',
        field: 'changeDate',
        size: 118,
        align: 'center',
        required: true,
        editable: (row) => {
          if (isApprove) return false;
          const st = row?.status;
          return !st || st === CHANGE_STATUS.DRAFT || st === CHANGE_STATUS.REJECTED;
        },
        showPresets: false,
      }),
      hiCol.roTextColumn({
        header: '등록일자',
        field: 'registerDate',
        size: 100,
        align: 'center',
      }),
      hiCol.roTextColumn({
        header: '승인일자',
        field: 'approveDate',
        size: 100,
        align: 'center',
      }),
      hiCol.edTextColumn({
        header: '적요',
        field: 'remark',
        size: 200,
        editable: !isApprove,
      }),
    ];
  }, [isApprove]);

  // 1:1 / N:N 디테일 컬럼 분리 — 명칭·현액에 ··· 코드/현액 조회
  const detailColumns = useMemo(() => {
    const edit = !isApprove;
    const nameSearchProps = (type, side, title) =>
      isApprove
        ? {}
        : {
            showSearch: true,
            searchTitle: title,
            onSearch: ({ row }) => openDetailLookup(type, row, side),
          };
    const balanceSearchProps = (side, title) =>
      isApprove
        ? {}
        : {
            showSearch: true,
            searchTitle: title,
            onSearch: ({ row }) => openDetailLookup('balance', row, side),
          };

    if (isNn) {
      return [
        hiCol.roTextColumn({
          header: '현액',
          field: 'balanceAmt',
          size: 100,
          align: 'right',
          ...balanceSearchProps('', '확정 현액 조회(지출)'),
        }),
        hiCol.edTextColumn({ header: '부서코드', field: 'deptCd', size: 90, editable: edit }),
        hiCol.edTextColumn({
          header: '부서명',
          field: 'deptNm',
          size: 110,
          editable: edit,
          ...nameSearchProps('dept', '', '부서 조회'),
        }),
        hiCol.edTextColumn({ header: '사업코드', field: 'projectCd', size: 100, editable: edit }),
        hiCol.edTextColumn({
          header: '사업명',
          field: 'projectNm',
          size: 120,
          editable: edit,
          ...nameSearchProps('project', '', '사업 조회'),
        }),
        hiCol.edTextColumn({ header: '과목코드', field: 'subjectCd', size: 100, editable: edit }),
        hiCol.edTextColumn({
          header: '과목명',
          field: 'subjectNm',
          size: 120,
          editable: edit,
          ...nameSearchProps('subject', '', '과목 조회'),
        }),
        hiCol.edTextColumn({ header: '재원코드', field: 'fundCd', size: 90, editable: edit }),
        hiCol.edTextColumn({
          header: '재원명',
          field: 'fundNm',
          size: 110,
          editable: edit,
          ...nameSearchProps('fund', '', '재원 조회'),
        }),
        hiCol.edNumberColumn({
          header: '증감금액(±)',
          field: 'changeAmt',
          size: 110,
          align: 'right',
          editable: edit,
          title: '감액은 음수 · 증액은 양수 · 합계 0',
        }),
      ];
    }
    return [
      hiCol.roTextColumn({
        header: '변경전 현액',
        field: 'beforeBalanceAmt',
        size: 100,
        align: 'right',
        ...balanceSearchProps('before', '변경전 현액 조회(지출)'),
      }),
      hiCol.edTextColumn({ header: '변경전 부서', field: 'beforeDeptCd', size: 90, editable: edit }),
      hiCol.edTextColumn({
        header: '변경전 부서명',
        field: 'beforeDeptNm',
        size: 110,
        editable: edit,
        ...nameSearchProps('dept', 'before', '부서 조회'),
      }),
      hiCol.edTextColumn({ header: '변경전 사업', field: 'beforeProjectCd', size: 90, editable: edit }),
      hiCol.edTextColumn({
        header: '변경전 사업명',
        field: 'beforeProjectNm',
        size: 110,
        editable: edit,
        ...nameSearchProps('project', 'before', '사업 조회'),
      }),
      hiCol.edTextColumn({ header: '변경전 과목', field: 'beforeSubjectCd', size: 90, editable: edit }),
      hiCol.edTextColumn({
        header: '변경전 과목명',
        field: 'beforeSubjectNm',
        size: 110,
        editable: edit,
        ...nameSearchProps('subject', 'before', '과목 조회'),
      }),
      hiCol.edTextColumn({ header: '변경전 재원', field: 'beforeFundCd', size: 90, editable: edit }),
      hiCol.edTextColumn({
        header: '변경전 재원명',
        field: 'beforeFundNm',
        size: 110,
        editable: edit,
        ...nameSearchProps('fund', 'before', '재원 조회'),
      }),
      hiCol.roTextColumn({
        header: '변경후 현액',
        field: 'afterBalanceAmt',
        size: 100,
        align: 'right',
        ...balanceSearchProps('after', '변경후 현액 조회(지출)'),
      }),
      hiCol.edTextColumn({ header: '변경후 부서', field: 'afterDeptCd', size: 90, editable: edit }),
      hiCol.edTextColumn({
        header: '변경후 부서명',
        field: 'afterDeptNm',
        size: 110,
        editable: edit,
        ...nameSearchProps('dept', 'after', '부서 조회'),
      }),
      hiCol.edTextColumn({ header: '변경후 사업', field: 'afterProjectCd', size: 90, editable: edit }),
      hiCol.edTextColumn({
        header: '변경후 사업명',
        field: 'afterProjectNm',
        size: 110,
        editable: edit,
        ...nameSearchProps('project', 'after', '사업 조회'),
      }),
      hiCol.edTextColumn({ header: '변경후 과목', field: 'afterSubjectCd', size: 90, editable: edit }),
      hiCol.edTextColumn({
        header: '변경후 과목명',
        field: 'afterSubjectNm',
        size: 110,
        editable: edit,
        ...nameSearchProps('subject', 'after', '과목 조회'),
      }),
      hiCol.edTextColumn({ header: '변경후 재원', field: 'afterFundCd', size: 90, editable: edit }),
      hiCol.edTextColumn({
        header: '변경후 재원명',
        field: 'afterFundNm',
        size: 110,
        editable: edit,
        ...nameSearchProps('fund', 'after', '재원 조회'),
      }),
      hiCol.edNumberColumn({
        header: '변경금액',
        field: 'changeAmt',
        size: 100,
        align: 'right',
        editable: edit,
      }),
    ];
  }, [isNn, isApprove, openDetailLookup]);

  const enrichMaster = useCallback((row) => {
    if (!row) return row;
    const n = normalizeMasterDates(row);
    return {
      ...n,
      statusNm: changeStatusLabel(n.status),
      changeTypeNm: changeTypeLabel(n.changeType),
    };
  }, []);

  const masterGrid = useHiGridHook({
    gridRef: masterRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: !isApprove,
    confirmCheck: !isApprove,
    defaultRow: emptyMasterRow({ matchMode }),
  });

  const detailGrid = useHiGridHook({
    gridRef: detailRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: !isApprove,
    confirmCheck: !isApprove,
    defaultRow: isNn ? emptyDetailNn() : emptyDetailOneToOne(),
  });

  const dimsFromSearch = useCallback(() => {
    const v = getValues();
    return {
      tenantCode,
      fiscalYear: String(v.fiscalYear || '').trim(),
      budgetKindCode: String(v.budgetKindCode || '').trim(),
      inoutDiv: String(v.inoutDiv || '지출').trim(),
    };
  }, [getValues, tenantCode]);

  const showDetailsFor = useCallback(
    (uid, allDetails) => {
      const list = (allDetails || bundleDetails).filter(
        (d) => d.changeUid === uid && d.mod !== 'D'
      );
      detailRef.current?.setData?.(list);
    },
    [bundleDetails]
  );

  const handleSearch = useCallback(async () => {
    const dims = dimsFromSearch();
    if (!dims.fiscalYear) {
      showAlert('회계연도를 입력하세요.');
      return;
    }
    if (!dims.budgetKindCode) {
      showAlert('예산차수를 선택하세요.');
      return;
    }
    const bundle = await loadChangeBundle(dims);
    const statusFilter = String(getValues().status || '').trim();
    const typeFilter = String(getValues().changeType || '').trim();
    let masters = isApprove
      ? filterMastersForApprove(bundle.masters, { status: statusFilter || undefined })
      : filterMastersForRegister(bundle.masters, { status: statusFilter || undefined });
    if (typeFilter) {
      masters = masters.filter((m) => m.changeType === typeFilter);
    }
    const enriched = masters.map((m) =>
      enrichMaster({
        ...m,
        changeAmt: calcMasterChangeAmt(m, bundle.details, matchMode),
        matchMode,
      })
    );
    setBundleDetails(bundle.details);
    masterRef.current?.setData?.(enriched);
    const first = enriched[0];
    const uid = first?.changeUid || '';
    setSelectedUid(uid);
    showDetailsFor(uid, bundle.details);
  }, [dimsFromSearch, getValues, isApprove, enrichMaster, matchMode, showDetailsFor]);

  useEffect(() => {
    reset(searchDefaults);
  }, [reset, searchDefaults]);

  const onMasterSelect = useCallback(
    (row) => {
      const uid = row?.changeUid || '';
      setSelectedUid(uid);
      showDetailsFor(uid, bundleDetails);
    },
    [bundleDetails, showDetailsFor]
  );

  useEffect(() => {
    masterGrid.onRowClicked = ({ data }) => onMasterSelect(data);
    masterGrid.onSelectRow = ({ data }) => onMasterSelect(data);
  }, [masterGrid, onMasterSelect]);

  const collectDetailFromGrid = useCallback(() => {
    const rows = detailRef.current?.getData?.() || [];
    const uid = selectedUidRef.current;
    return rows
      .filter((r) => r.mod !== 'D')
      .map((r) => ({
        ...r,
        changeUid: uid,
        detailUid: r.detailUid || createChangeDetailUid(),
      }));
  }, []);

  const mergeDetailsForSave = useCallback((allDetails, uid, nextForUid) => {
    const others = (allDetails || []).filter((d) => d.changeUid !== uid);
    return [...others, ...nextForUid];
  }, []);

  const handleSave = useCallback(async () => {
    if (isApprove) {
      showAlert('승인 화면에서는 저장 대신 승인/반려을 사용하세요.');
      return;
    }
    const dims = dimsFromSearch();
    if (!dims.fiscalYear || !dims.budgetKindCode) {
      showAlert('조회조건(회계연도·예산차수)을 확인하세요.');
      return;
    }

    let allDetails = [...bundleDetails];
    if (selectedUidRef.current) {
      const curDetails = collectDetailFromGrid();
      allDetails = mergeDetailsForSave(allDetails, selectedUidRef.current, curDetails);
    }

    const masterRows = (masterRef.current?.getData?.() || []).map((m) => {
      const uid = m.changeUid || createChangeUid();
      const changeAmt = calcMasterChangeAmt({ ...m, changeUid: uid }, allDetails, matchMode);
      const registerDate = String(m.registerDate || '').trim() || todayYmd();
      return enrichMaster({
        ...m,
        changeUid: uid,
        changeNo: m.changeNo || nextChangeNo(masterRef.current?.getData?.() || []),
        changeAmt,
        matchMode,
        status: m.status || CHANGE_STATUS.DRAFT,
        registerDate,
        mod: m.mod === 'A' || !m.changeUid ? 'A' : m.mod || 'U',
      });
    });

    const masterUids = new Set(masterRows.map((m) => m.changeUid));
    allDetails = allDetails.filter((d) => masterUids.has(d.changeUid) || d.mod === 'D');

    const v = validateChangeBundle(masterRows, allDetails, matchMode);
    if (!v.ok) {
      showAlert(v.message, 'warning');
      return;
    }

    const saved = await saveChangeBundle(dims, masterRows, allDetails);
    setBundleDetails(saved.details);
    masterRef.current?.setData?.(
      saved.masters.map((m) =>
        enrichMaster({
          ...m,
          changeAmt: calcMasterChangeAmt(m, saved.details, matchMode),
        })
      )
    );
    if (selectedUidRef.current) showDetailsFor(selectedUidRef.current, saved.details);
    showAlert('저장했습니다.', 'success');
  }, [
    isApprove,
    dimsFromSearch,
    bundleDetails,
    collectDetailFromGrid,
    mergeDetailsForSave,
    matchMode,
    enrichMaster,
    showDetailsFor,
  ]);

  const handleRequest = useCallback(async () => {
    if (isApprove) return;
    const uid = selectedUidRef.current;
    if (!uid) {
      showAlert('승인요청할 마스터를 선택하세요.');
      return;
    }
    const masters = masterRef.current?.getData?.() || [];
    const row = masters.find((m) => m.changeUid === uid);
    if (!row) return;
    if (row.status !== CHANGE_STATUS.DRAFT && row.status !== CHANGE_STATUS.REJECTED) {
      showAlert('작성중·반려 건만 승인요청할 수 있습니다.');
      return;
    }

    const allDetails = mergeDetailsForSave(bundleDetails, uid, collectDetailFromGrid());
    const withAmt = masters.map((m) => ({
      ...m,
      changeAmt: calcMasterChangeAmt(m, allDetails, matchMode),
    }));
    const v = validateChangeBundle(withAmt, allDetails, matchMode);
    if (!v.ok) {
      showAlert(v.message, 'warning');
      return;
    }
    const dets = allDetails.filter((d) => d.changeUid === uid && d.mod !== 'D');
    if (!dets.length) {
      showAlert('디테일을 입력한 뒤 승인요청하세요.');
      return;
    }

    const ok = await showConfirm('선택한 변경 건을 승인요청할까요?');
    if (!ok) return;

    const dims = dimsFromSearch();
    const { masters: nextM, details: nextD } = requestApproval(withAmt, allDetails, uid);
    await saveChangeBundle(dims, nextM, nextD);
    await handleSearch();
    showAlert('승인요청했습니다. 예산변경승인 화면에서 처리하세요.', 'success');
  }, [
    isApprove,
    bundleDetails,
    collectDetailFromGrid,
    mergeDetailsForSave,
    matchMode,
    dimsFromSearch,
    handleSearch,
  ]);

  const handleApprove = useCallback(async () => {
    if (!isApprove) return;
    const uid = selectedUidRef.current;
    if (!uid) {
      showAlert('승인할 건을 선택하세요.');
      return;
    }
    const masters = masterRef.current?.getData?.() || [];
    const row = masters.find((m) => m.changeUid === uid);
    if (!row || row.status !== CHANGE_STATUS.REQUESTED) {
      showAlert('승인요청 상태인 건만 승인할 수 있습니다.');
      return;
    }
    const assignMsg =
      row.assignProcessYn === 'Y'
        ? '배정처리=Y → 승인과 함께 배정처리에 변경번호를 남겨 추적합니다.'
        : '배정처리=N → 변경만 승인합니다. 배정은 예산배정 화면에서 별도 처리하세요.';
    const ok = await showConfirm(`승인할까요?\n${assignMsg}`);
    if (!ok) return;

    const dims = dimsFromSearch();
    const bundle = await loadChangeBundle(dims);
    const { masters: nextM, details: nextD } = approveChange(bundle.masters, bundle.details, uid, {
      approveAmt: row.approveAmt || row.changeAmt,
    });
    await saveChangeBundle(dims, nextM, nextD);

    const done = nextM.find((m) => m.changeUid === uid);
    if (done?.assignProcessYn === 'Y') {
      const lines = buildAllocLinesFromChange(done, nextD, matchMode);
      await appendAllocFromBudgetChange(dims, lines, { userId: user?.id || user?.userId || '' });
    }

    await handleSearch();
    showAlert(
      done?.assignProcessYn === 'Y'
        ? '승인했습니다. 배정처리에 변경번호가 반영되었습니다.'
        : '승인했습니다. 배정은 별도 처리하세요.',
      'success'
    );
  }, [isApprove, dimsFromSearch, handleSearch, matchMode, user]);

  const handleReject = useCallback(async () => {
    if (!isApprove) return;
    const uid = selectedUidRef.current;
    if (!uid) {
      showAlert('반려할 건을 선택하세요.');
      return;
    }
    const masters = masterRef.current?.getData?.() || [];
    const row = masters.find((m) => m.changeUid === uid);
    if (!row || row.status !== CHANGE_STATUS.REQUESTED) {
      showAlert('승인요청 상태인 건만 반려할 수 있습니다.');
      return;
    }
    const ok = await showConfirm('반려할까요?');
    if (!ok) return;
    const dims = dimsFromSearch();
    const bundle = await loadChangeBundle(dims);
    const { masters: nextM, details: nextD } = rejectChange(
      bundle.masters,
      bundle.details,
      uid,
      '반려'
    );
    await saveChangeBundle(dims, nextM, nextD);
    await handleSearch();
    showAlert('반려했습니다.', 'success');
  }, [isApprove, dimsFromSearch, handleSearch]);

  const dispatchMasterAdd = useCallback(async () => {
    if (isApprove) return;
    const rows = masterRef.current?.getData?.() || [];
    const uid = createChangeUid();
    const row = enrichMaster(
      emptyMasterRow({
        changeUid: uid,
        changeNo: nextChangeNo(rows),
        matchMode,
        // changeDate 비움 — 사용자가 캘린더로 지정(소급). 오늘 자동입력 금지
        mod: 'A',
      })
    );
    masterRef.current?.setData?.([...rows, row]);
    setSelectedUid(uid);
    detailRef.current?.setData?.([]);
  }, [isApprove, enrichMaster, matchMode]);

  const dispatchDetailAdd = useCallback(async () => {
    if (isApprove) return;
    const uid = selectedUidRef.current;
    if (!uid) {
      showAlert('마스터를 먼저 선택하세요.');
      return;
    }
    const rows = detailRef.current?.getData?.() || [];
    const row = isNn
      ? emptyDetailNn({
          detailUid: createChangeDetailUid(),
          changeUid: uid,
          mod: 'A',
        })
      : emptyDetailOneToOne({
          detailUid: createChangeDetailUid(),
          changeUid: uid,
          mod: 'A',
        });
    detailRef.current?.setData?.([...rows, row]);
  }, [isApprove, isNn]);

  // 공통 CRUD(F7~F10) · 화면 전용은 extraButtons
  const actionButtons = useMemo(() => {
    if (isApprove) {
      return [{ id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' }];
    }
    return [
      { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' },
      {
        id: 'add',
        label: '추가',
        icon: '➕',
        onClick: handleActiveAdd,
        hotkey: 'F8',
        title: activeLabel
          ? `추가 → ${activeLabel} (F8)`
          : '추가 (F8) · 그리드 선택 Alt+F2',
      },
      {
        id: 'delete',
        label: '삭제',
        icon: '❌',
        onClick: handleActiveDelete,
        hotkey: 'F9',
        variant: 'danger',
        title: activeLabel
          ? `삭제 → ${activeLabel} (F9)`
          : '삭제 (F9) · 그리드 선택 Alt+F2',
      },
      {
        id: 'save',
        label: '저장',
        icon: '✅',
        onClick: handleSave,
        hotkey: 'F10',
        variant: 'success',
        title: '저장 (F10) · 화면 전체',
      },
    ];
  }, [
    isApprove,
    handleSearch,
    handleActiveAdd,
    handleActiveDelete,
    handleSave,
    activeLabel,
  ]);

  const extraButtons = useMemo(() => {
    if (isApprove) {
      return [
        {
          id: 'approve',
          label: '승인',
          onClick: handleApprove,
          variant: 'primary',
          title: '배정처리=Y면 배정처리에 변경번호 기록',
        },
        {
          id: 'reject',
          label: '반려',
          onClick: handleReject,
          variant: 'danger',
        },
      ];
    }
    return [
      {
        id: 'request',
        label: '승인요청',
        onClick: handleRequest,
        title: '작성중·반려 건 → 예산변경승인',
      },
    ];
  }, [isApprove, handleApprove, handleReject, handleRequest]);

  // FormArea fields는 「행 배열」— 각 행이 필드 객체 배열
  const searchFields = useMemo(
    () => [
      [
        {
          name: 'fiscalYear',
          label: '회계연도',
          type: 'fiscalYear',
          width: '88px',
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'budgetKindCode',
          label: '예산차수',
          type: 'select',
          options: kindOptions,
          width: '140px',
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'inoutDiv',
          label: '수지구분',
          type: 'select',
          options: [
            { value: '지출', label: '지출' },
            { value: '수입', label: '수입' },
          ],
          width: '90px',
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'changeType',
          label: '변경구분',
          type: 'select',
          options: [{ value: '', label: '전체' }, ...CHANGE_TYPE_OPTIONS],
          width: '110px',
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'status',
          label: '상태',
          type: 'select',
          options: [
            { value: '', label: isApprove ? '요청·승인·반려' : '전체' },
            ...CHANGE_STATUS_OPTIONS.filter((o) =>
              isApprove ? o.value !== CHANGE_STATUS.DRAFT : true
            ),
          ],
          width: '120px',
          labelWidth: SEARCH_LABEL_W,
        },
      ],
    ],
    [kindOptions, isApprove]
  );

  const lookupConfig = useMemo(() => {
    const dims = dimsFromSearch();
    const tenant = dims.tenantCode || tenantCode;
    if (lookup.type === 'dept') {
      const rows = filterDepts(loadDepts(tenant), { useYn: 'Y' }).map((r) => ({
        ...r,
        id: r.deptUid || r.deptCode,
        code: r.deptCode,
        name: r.deptName,
      }));
      return {
        title: '부서 조회',
        rows,
        columns: [
          hiCol.roTextColumn({ header: '부서코드', field: 'deptCode', size: 110 }),
          hiCol.roTextColumn({ header: '부서명', field: 'deptName', size: 160 }),
        ],
        enableSearch: true,
        searchConfig: {
          mode: 'unified',
          keywordFields: ['deptCode', 'deptName', 'code', 'name'],
        },
      };
    }
    if (lookup.type === 'project') {
      const list = loadOrSeedProjects(dims) || [];
      const rows = list
        .filter((r) => r.useYn !== 'N')
        .map((r) => ({
          ...r,
          id: r.projectUid || r.projectCode,
          code: r.projectCode,
          name: r.projectName,
        }));
      return {
        title: '사업 조회',
        rows,
        columns: [
          hiCol.roTextColumn({ header: '사업코드', field: 'projectCode', size: 110 }),
          hiCol.roTextColumn({ header: '사업명', field: 'projectName', size: 180 }),
        ],
        enableSearch: true,
        searchConfig: {
          mode: 'unified',
          keywordFields: ['projectCode', 'projectName', 'code', 'name'],
        },
      };
    }
    if (lookup.type === 'subject') {
      const all = loadSubjectsByFilter({
        ...dims,
        inoutDiv: dims.inoutDiv || '지출',
      });
      const finals = (all || []).filter((r) => r.finalYn === 'Y' && r.useYn !== 'N' && r.mod !== 'D');
      const rows = finals.map((r) => {
        const disp = buildSubjectDispCode(r, all);
        const dispName = buildSubjectDispName(r, all);
        return {
          ...r,
          id: r.subjectUid || `${r.budgetType}-${r.subjectCode}`,
          code: disp,
          name: dispName,
          subjectDispCode: disp,
          subjectDispName: dispName,
          subjectName: dispName,
        };
      });
      return {
        title: '과목 조회',
        rows,
        columns: [
          hiCol.roTextColumn({ header: '결합코드', field: 'subjectDispCode', size: 140 }),
          hiCol.roTextColumn({ header: '과목명', field: 'subjectDispName', size: 260 }),
        ],
        enableSearch: true,
        searchConfig: {
          mode: 'unified',
          keywordFields: [
            'subjectDispCode',
            'subjectCode',
            'subjectDispName',
            'subjectName',
            'code',
            'name',
          ],
        },
      };
    }
    if (lookup.type === 'fund') {
      const rows = loadFunds(dims)
        .filter((r) => r.useYn !== 'N')
        .map((r) => ({
          ...r,
          id: r.fundUid || r.fundCode,
          code: r.fundCode,
          name: r.fundName,
        }));
      return {
        title: '재원 조회',
        rows,
        columns: [
          hiCol.roTextColumn({ header: '재원코드', field: 'fundCode', size: 100 }),
          hiCol.roTextColumn({ header: '재원명', field: 'fundName', size: 140 }),
        ],
        enableSearch: true,
        searchConfig: {
          mode: 'unified',
          keywordFields: ['fundCode', 'fundName', 'code', 'name'],
        },
      };
    }
    if (lookup.type === 'balance') {
      // 확정 현액 전체(지출만) — 축 일괄 적재용
      const all = loadBalances({
        tenantCode: tenant,
        fiscalYear: dims.fiscalYear,
      });
      const rows = filterBalances(all, { inoutDiv: '지출' }).map((r) => ({
        ...r,
        id: r.balanceUid || `${r.deptCode}-${r.projectCode}-${r.subjectCode}-${r.fundCode}`,
        code: r.subjectDispCode || r.subjectCode || '',
        name: r.subjectName || '',
      }));
      return {
        title: '현액 조회 (지출·확정)',
        rows,
        columns: [
          hiCol.roTextColumn({ header: '차수', field: 'budgetKindCode', size: 70, align: 'center' }),
          hiCol.roTextColumn({ header: '부서', field: 'deptName', size: 100 }),
          hiCol.roTextColumn({ header: '사업', field: 'projectName', size: 140 }),
          hiCol.roTextColumn({ header: '과목코드', field: 'subjectDispCode', size: 100 }),
          hiCol.roTextColumn({ header: '과목명', field: 'subjectName', size: 140 }),
          hiCol.roTextColumn({ header: '재원', field: 'fundName', size: 80 }),
          hiCol.roNumberColumn({ header: '확정편성', field: 'confirmedAmt', size: 100 }),
          hiCol.roNumberColumn({ header: '현액', field: 'balanceAmt', size: 100 }),
          hiCol.roNumberColumn({ header: '가용', field: 'availableAmt', size: 100 }),
        ],
        enableSearch: true,
        searchConfig: {
          mode: 'unified',
          keywordLabel: '검색',
          keywordFields: [
            'deptCode',
            'deptName',
            'projectCode',
            'projectName',
            'subjectDispCode',
            'subjectCode',
            'subjectName',
            'fundCode',
            'fundName',
            'budgetKindCode',
          ],
        },
      };
    }
    return { title: '조회', rows: [], columns: [] };
  }, [lookup.type, dimsFromSearch, tenantCode]);

  const applyLookup = useCallback(
    (payload) => {
      const row = Array.isArray(payload) ? payload[0] : payload;
      if (!row || !lookup.detailUid) {
        setLookup({ open: false, type: null, side: '', detailUid: '' });
        return;
      }
      const side = lookup.side;
      const prefix = side === 'before' ? 'before' : side === 'after' ? 'after' : '';

      if (lookup.type === 'balance') {
        const amt = row.balanceAmt != null ? Number(row.balanceAmt) : Number(row.confirmedAmt) || 0;
        const axes = {
          deptCd: row.deptCode || '',
          deptNm: row.deptName || '',
          projectCd: row.projectCode || '',
          projectNm: row.projectName || '',
          subjectCd: row.subjectDispCode || row.subjectCode || '',
          subjectNm: row.subjectName || '',
          fundCd: row.fundCode || '',
          fundNm: row.fundName || '',
        };
        if (prefix === 'before') {
          patchDetailRow(lookup.detailUid, {
            beforeBalanceUid: row.balanceUid || '',
            beforeBalanceAmt: amt,
            beforeDeptCd: axes.deptCd,
            beforeDeptNm: axes.deptNm,
            beforeProjectCd: axes.projectCd,
            beforeProjectNm: axes.projectNm,
            beforeSubjectCd: axes.subjectCd,
            beforeSubjectNm: axes.subjectNm,
            beforeFundCd: axes.fundCd,
            beforeFundNm: axes.fundNm,
          });
        } else if (prefix === 'after') {
          patchDetailRow(lookup.detailUid, {
            afterBalanceUid: row.balanceUid || '',
            afterBalanceAmt: amt,
            afterDeptCd: axes.deptCd,
            afterDeptNm: axes.deptNm,
            afterProjectCd: axes.projectCd,
            afterProjectNm: axes.projectNm,
            afterSubjectCd: axes.subjectCd,
            afterSubjectNm: axes.subjectNm,
            afterFundCd: axes.fundCd,
            afterFundNm: axes.fundNm,
          });
        } else {
          patchDetailRow(lookup.detailUid, {
            balanceUid: row.balanceUid || '',
            balanceAmt: amt,
            ...axes,
          });
        }
      } else if (lookup.type === 'dept') {
        const cd = row.deptCode || '';
        const nm = row.deptName || '';
        if (prefix === 'before') patchDetailRow(lookup.detailUid, { beforeDeptCd: cd, beforeDeptNm: nm });
        else if (prefix === 'after') patchDetailRow(lookup.detailUid, { afterDeptCd: cd, afterDeptNm: nm });
        else patchDetailRow(lookup.detailUid, { deptCd: cd, deptNm: nm });
      } else if (lookup.type === 'project') {
        const cd = row.projectCode || '';
        const nm = row.projectName || '';
        if (prefix === 'before') {
          patchDetailRow(lookup.detailUid, { beforeProjectCd: cd, beforeProjectNm: nm });
        } else if (prefix === 'after') {
          patchDetailRow(lookup.detailUid, { afterProjectCd: cd, afterProjectNm: nm });
        } else {
          patchDetailRow(lookup.detailUid, { projectCd: cd, projectNm: nm });
        }
      } else if (lookup.type === 'subject') {
        const dims = dimsFromSearch();
        const all = loadSubjectsByFilter({ ...dims, inoutDiv: dims.inoutDiv || '지출' });
        const cd = row.subjectDispCode || buildSubjectDispCode(row, all) || row.subjectCode || '';
        const nm = row.subjectDispName || row.subjectName || buildSubjectDispName(row, all) || '';
        if (prefix === 'before') {
          patchDetailRow(lookup.detailUid, { beforeSubjectCd: cd, beforeSubjectNm: nm });
        } else if (prefix === 'after') {
          patchDetailRow(lookup.detailUid, { afterSubjectCd: cd, afterSubjectNm: nm });
        } else {
          patchDetailRow(lookup.detailUid, { subjectCd: cd, subjectNm: nm });
        }
      } else if (lookup.type === 'fund') {
        const cd = row.fundCode || '';
        const nm = row.fundName || '';
        if (prefix === 'before') patchDetailRow(lookup.detailUid, { beforeFundCd: cd, beforeFundNm: nm });
        else if (prefix === 'after') patchDetailRow(lookup.detailUid, { afterFundCd: cd, afterFundNm: nm });
        else patchDetailRow(lookup.detailUid, { fundCd: cd, fundNm: nm });
      }

      setLookup({ open: false, type: null, side: '', detailUid: '' });
    },
    [lookup.type, lookup.side, lookup.detailUid, patchDetailRow, dimsFromSearch]
  );

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} extraButtons={extraButtons} />
        <Stack>
          <CollapsibleSearchPanel
            storageKey={`budget-change-${screenMode}`}
            prefsUserId={prefsUserId}
            title="조회조건"
          >
            <FormArea compact fields={searchFields} />
          </CollapsibleSearchPanel>

          <Hint>
            매칭: {changeMatchModeLabel(matchMode)}
            {activeLabel ? ` · 활성그리드: ${activeLabel}` : ''}
            {isApprove
              ? ' · 승인요청 건을 선택 후 승인/반려'
              : ' · 명칭·현액 ··· 조회 · 변경일자 직접 지정 · Alt+F2'}
          </Hint>

          <Panel $flex={1.1}>
            <ActiveGridScope
              gridId={MASTER_GRID_ID}
              label="변경 마스터"
              order={10}
              onAdd={dispatchMasterAdd}
              onDelete={async () => {
                await masterGrid.handleDelete?.();
              }}
              canAdd={!isApprove}
              canDelete={!isApprove}
            >
              <ActiveGridHeader gridId={MASTER_GRID_ID}>변경 마스터</ActiveGridHeader>
              <GridBody>
                <HiGridComponent
                  ref={masterRef}
                  columns={masterColumns}
                  showRowNumber
                  rowNumberSize={48}
                  enableColumnFilters
                  enableContextMenu={!isApprove}
                  enableRowEditMenu={!isApprove}
                  enableCut={!isApprove}
                  enableFindReplace
                  allowReplace={!isApprove}
                  enableHeaderMenu
                  enableSelectionStats
                  enableFreeze
                  enableQuickExport
                  exportFileName="예산변경마스터"
                  gridId={`budget-change-master-${screenMode}`}
                  prefsUserId={prefsUserId}
                  headerActive={activeGridId === MASTER_GRID_ID}
                  {...(isApprove ? {} : masterGrid.contextMenuProps || {})}
                />
              </GridBody>
            </ActiveGridScope>
          </Panel>

          <Panel $flex={1.2}>
            <ActiveGridScope
              gridId={DETAIL_GRID_ID}
              label="변경 디테일"
              order={20}
              onAdd={dispatchDetailAdd}
              onDelete={async () => {
                await detailGrid.handleDelete?.();
              }}
              canAdd={!isApprove}
              canDelete={!isApprove}
            >
              <ActiveGridHeader gridId={DETAIL_GRID_ID}>
                {isNn
                  ? '변경 디테일 (N:N · 축별 ±증감, 합계 0)'
                  : '변경 디테일 (1:1 · 변경전→변경후)'}
              </ActiveGridHeader>
              <GridBody>
                <HiGridComponent
                  ref={detailRef}
                  columns={detailColumns}
                  showRowNumber
                  rowNumberSize={48}
                  enableColumnFilters
                  enableContextMenu={!isApprove}
                  enableRowEditMenu={!isApprove}
                  enableCut={!isApprove}
                  enableFindReplace
                  allowReplace={!isApprove}
                  enableHeaderMenu
                  enableSelectionStats
                  enableFreeze
                  enableQuickExport
                  exportFileName="예산변경디테일"
                  gridId={`budget-change-detail-${screenMode}`}
                  prefsUserId={prefsUserId}
                  headerActive={activeGridId === DETAIL_GRID_ID}
                  {...(isApprove ? {} : detailGrid.contextMenuProps || {})}
                />
              </GridBody>
            </ActiveGridScope>
          </Panel>
        </Stack>
      </FormProvider>

      <CodeLookupDialog
        open={lookup.open}
        title={lookupConfig.title}
        columns={lookupConfig.columns}
        rows={lookupConfig.rows}
        enableSearch={!!lookupConfig.enableSearch}
        searchConfig={lookupConfig.searchConfig}
        multiSelect={false}
        prefsUserId={prefsUserId}
        sizeKey={lookup.type ? `codeLookup-${lookup.type}` : 'codeLookup'}
        onSelect={applyLookup}
        onClose={() => setLookup({ open: false, type: null, side: '', detailUid: '' })}
      />
    </PageContainer>
  );
}

export default function BudgetChangeWorkspace({ screenMode = 'register' }) {
  return (
    <ActiveGridProvider>
      <BudgetChangeWorkspaceInner screenMode={screenMode} />
    </ActiveGridProvider>
  );
}
